import { ReactNode } from 'react';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { cockpitTokens } from './cockpitTokens';
import { Count, countOf, usePullRequestLists } from './pullRequestLists';
import { useHomeSources } from './homeSources';
import { CI_WIDGET_ANCHOR_ID, scrollToAnchor } from './anchors';
import type { DeploymentsSnapshot } from '../deployments/snapshot';

/**
 * My workspace (DX-307, DX-546): four figures, each reaching what it counts.
 *
 * It replaces the counts row that stood above the cards (DX-71, DX-74), and
 * keeps that row's one rule: every figure comes from a source the portal has,
 * and a figure the page could not determine reads as a played-down dash, never
 * as a zero (specs/home-page.md AC-4, AC-5).
 *
 *   - Owned components → the catalog's components owned by the reader's team.
 *                        A dash while the reader is in none (AC-28): that is
 *                        the state a new colleague is in until a GitHub team
 *                        has them, and a zero there would read as "your team
 *                        owns nothing". The card beside the figure says so.
 *   - Open reviews     → the reviews waiting on the reader, the same answer
 *                        the pull-request card's review tab reads.
 *   - Deployments      → the deploys of the components the reader owns over
 *                        the window the deployments snapshot covers — the
 *                        same rows the Recent deployments card shows with
 *                        "My components" on and no other filter. It counted
 *                        every deploy of the day until DX-546; a figure in
 *                        "My workspace" that was not about the reader's
 *                        workspace.
 *   - CI success       → the CI snapshot's success rate across all teams,
 *                        with the window it covers — the headline figure of
 *                        the CI card below, which has no per-team answer yet.
 *                        The caption says "all teams" so it is not read as
 *                        the reader's own.
 *
 * MUI v4 only.
 */

const useStyles = makeStyles(theme => ({
  // No frame of its own: the home page's workspace card around it carries the
  // frame and the title.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  cell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
    minWidth: 0,
    minHeight: 64,
    padding: theme.spacing(1.5, 2),
    textAlign: 'left' as const,
    textDecoration: 'none',
    color: 'inherit',
    font: 'inherit',
    background: 'transparent',
    border: 'none',
    borderRight: `1px solid ${cockpitTokens.color.border}`,
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
    cursor: 'pointer',
    '&:nth-child(2n)': { borderRight: 'none' },
    '&:nth-child(n+3)': { borderBottom: 'none' },
    '&:hover': {
      background: cockpitTokens.color.surfaceStrong,
      textDecoration: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.borderHover}`,
      outlineOffset: -2,
    },
  },
  label: {
    fontSize: '0.8rem',
    color: cockpitTokens.color.textMuted,
  },
  figure: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap' as const,
    columnGap: theme.spacing(0.75),
    marginTop: 'auto',
  },
  value: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.1,
    color: cockpitTokens.color.text,
    letterSpacing: '-0.01em',
  },
  valueMuted: {
    color: cockpitTokens.color.textFaint,
  },
  caption: {
    fontSize: '0.75rem',
    color: cockpitTokens.color.textFaint,
  },
}));

const Cell = ({
  label,
  value,
  caption,
  muted,
  to,
  onActivate,
  state,
}: {
  label: string;
  value: ReactNode;
  caption?: string;
  muted?: boolean;
  /** `data-state`, for what the figure is when it is not a number. */
  state?: string;
} & (
  { to: string; onActivate?: never } | { to?: never; onActivate: () => void }
)) => {
  const classes = useStyles();
  const body = (
    <>
      <Typography component="span" className={classes.label}>
        {label}
      </Typography>
      <span className={classes.figure}>
        <Typography
          component="span"
          className={`${classes.value} ${muted ? classes.valueMuted : ''}`}
          data-muted={muted ? 'true' : undefined}
          data-state={state}
        >
          {value}
        </Typography>
        {caption && (
          <Typography component="span" className={classes.caption}>
            {caption}
          </Typography>
        )}
      </span>
    </>
  );
  // A figure whose detail is a card on this page is a button that scrolls to
  // it; one whose detail is a page of its own is a link.
  return to ? (
    <Link to={to} className={classes.cell}>
      {body}
    </Link>
  ) : (
    <button type="button" className={classes.cell} onClick={onActivate}>
      {body}
    </button>
  );
};

const spinner = (
  <CircularProgress
    size={18}
    style={{ color: cockpitTokens.color.textFaint }}
  />
);

const shown = (figure: Count, unit = ''): ReactNode => {
  if (figure === 'loading') return spinner;
  if (figure === 'unknown') return '—';
  return `${figure}${unit}`;
};

/**
 * The deploys in the snapshot of the components with the given repository
 * slugs — the reader's own, over the snapshot's whole window.
 */
export const ownDeployments = (
  snapshot: DeploymentsSnapshot,
  slugs: Iterable<string>,
): number => {
  const mine = new Set([...slugs].map(slug => slug.toLowerCase()));
  return snapshot.deployments.filter(d => mine.has(d.slug.toLowerCase()))
    .length;
};

export const MyWorkspace = () => {
  const classes = useStyles();
  const { reader, owned, ci, deployments } = useHomeSources();
  const { reviews, showReviews } = usePullRequestLists();

  // Owned components. A reader in no team who owns nothing is a state of its
  // own: the figure is not unknown — the question cannot be asked of a team
  // that is not set — and it is not zero either. One who owns components by
  // their own user is counted like anyone else: the card below lists them, and
  // a dash above that list would contradict it (AC-28).
  let ownedFigure: Count;
  if (reader.loading || owned.loading) ownedFigure = 'loading';
  else if (reader.error || owned.error || !owned.data) ownedFigure = 'unknown';
  else ownedFigure = owned.data.length;
  const teamNotSet =
    reader.data !== undefined && !reader.data.inTeam && ownedFigure === 0;

  const reviewFigure: Count = countOf(reviews);

  // A snapshot not collected yet is not a week without deploys (AC-5), and
  // the figure needs the owned components too: without them there is nothing
  // to count the deploys against.
  let deployFigure: Count;
  if (deployments.loading || ownedFigure === 'loading')
    deployFigure = 'loading';
  else if (
    deployments.error ||
    !deployments.data?.generatedAt ||
    ownedFigure === 'unknown'
  ) {
    deployFigure = 'unknown';
  } else {
    deployFigure = ownDeployments(
      deployments.data,
      (owned.data ?? []).flatMap(c => c.slug ?? []),
    );
  }
  const deployWindow = deployments.data?.windowDays;

  // Nor is a snapshot with no repository in it a success rate of 0%.
  let ciFigure: Count;
  if (ci.loading) ciFigure = 'loading';
  else if (
    ci.error ||
    !ci.data?.generatedAt ||
    (ci.data?.totals.repos ?? 0) === 0
  ) {
    ciFigure = 'unknown';
  } else ciFigure = Math.round(ci.data.totals.successRate * 100);
  const ciWindow = ci.data?.windowDays;

  return (
    <div className={classes.grid} role="group" aria-label="My workspace">
      {teamNotSet ? (
        // No caption: the card beside the figures says what the dash means
        // (DX-546), and a caption here was one of three places saying it.
        <Cell
          label="Owned components"
          value="—"
          muted
          state="team-not-set"
          to="/access"
        />
      ) : (
        <Cell
          label="Owned components"
          value={shown(ownedFigure)}
          muted={ownedFigure === 'unknown'}
          to="/catalog?filters%5Bkind%5D=component&filters%5Buser%5D=owned"
        />
      )}
      <Cell
        label="Open reviews"
        value={shown(reviewFigure)}
        muted={reviewFigure === 'unknown'}
        onActivate={showReviews}
      />
      <Cell
        label="Deployments"
        value={teamNotSet ? '—' : shown(deployFigure)}
        muted={teamNotSet || deployFigure === 'unknown'}
        state={teamNotSet ? 'team-not-set' : undefined}
        caption={
          !teamNotSet && typeof deployFigure === 'number'
            ? `${deployWindow ? `${deployWindow}d · ` : ''}your components`
            : undefined
        }
        to="/deployments"
      />
      <Cell
        label="CI success"
        value={shown(ciFigure, '%')}
        muted={ciFigure === 'unknown'}
        caption={
          typeof ciFigure === 'number'
            ? `${ciWindow ? `${ciWindow}d · ` : ''}all teams`
            : undefined
        }
        onActivate={() => scrollToAnchor(CI_WIDGET_ANCHOR_ID)}
      />
    </div>
  );
};
